<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useSetlistStore } from '../../stores/setlistStore'
import { useSongStore } from '../../stores/songStore'
import { useBandStore } from '../../stores/bandStore'
import draggable from 'vuedraggable'

const emit = defineEmits(['back', 'open-teleprompter'])

const setlistStore = useSetlistStore()
const songStore = useSongStore()
const bandStore = useBandStore()

const setlist = computed(() => setlistStore.currentSetlist)
const songs = computed({
  get: () => setlistStore.currentSetlist?.songs || [],
  set: (val) => {
    if (setlistStore.currentSetlist) {
      setlistStore.currentSetlist.songs = val
    }
  }
})
const loading = computed(() => setlistStore.detailLoading)

const props = defineProps<{
  setlistId: number
}>()

onMounted(async () => {
  await setlistStore.fetchSetlistDetails(props.setlistId)
})

// Add song
const selectedSongId = ref<number | null>(null)
const addingSong = ref(false)

const availableSongs = computed(() => {
  const currentSongIds = new Set(songs.value.map(s => s.songId))
  return songStore.songs.filter(s => !currentSongIds.has(s.id))
})

const handleAddSong = async () => {
  if (!selectedSongId.value || !setlist.value) return
  addingSong.value = true
  try {
    await setlistStore.addSongToSetlist(setlist.value.id, selectedSongId.value)
    selectedSongId.value = null
  } finally {
    addingSong.value = false
  }
}

// Remove song
const showRemoveConfirm = ref(false)
const songToRemove = ref<{ id: number; title: string } | null>(null)

const confirmRemoveSong = (setlistSongId: number, songTitle: string) => {
  songToRemove.value = { id: setlistSongId, title: songTitle }
  showRemoveConfirm.value = true
}

const handleRemoveSong = async () => {
  if (!songToRemove.value) return
  try {
    await setlistStore.removeSongFromSetlist(songToRemove.value.id)
    showRemoveConfirm.value = false
    songToRemove.value = null
  } catch (error) {
    console.error(error)
  }
}

// Reorder
const handleReorder = async () => {
  if (!setlist.value) return
  const ids = songs.value.map(s => s.id)
  try {
    await setlistStore.reorderSetlistSongs(setlist.value.id, ids)
  } catch (error) {
    console.error(error)
  }
}

// Vote
const votingId = ref<number | null>(null)
const handleVote = async (setlistSongId: number, isLike: boolean) => {
  votingId.value = setlistSongId
  try {
    await setlistStore.voteOnSong(setlistSongId, isLike)
  } catch (error) {
    console.error(error)
  } finally {
    votingId.value = null
  }
}

// Edit name
const showEditDialog = ref(false)
const editName = ref('')
const isUpdating = ref(false)

const openEditDialog = () => {
  if (!setlist.value) return
  editName.value = setlist.value.name
  showEditDialog.value = true
}

const handleUpdateSetlist = async () => {
  if (!setlist.value || !editName.value) return
  isUpdating.value = true
  try {
    await setlistStore.updateSetlist(setlist.value.id, { name: editName.value })
    showEditDialog.value = false
  } catch (error) {
    console.error(error)
  } finally {
    isUpdating.value = false
  }
}

const handleBack = () => {
  setlistStore.currentSetlist = null
  emit('back')
}
</script>

<template>
  <div class="space-y-6 p-1 lg:p-8 bg-slate-200 min-h-full rounded-xl">
    <!-- Loading State -->
    <div v-if="loading || !setlist" class="py-20 text-center flex flex-col items-center justify-center rd-card-inner border-slate-300">
      <ProgressSpinner style="width: 40px; height: 40px" strokeWidth="6" fill="transparent"
          animationDuration=".5s" aria-label="Loading Setlist" />
      <p class="mt-4 text-sm text-slate-400 font-medium animate-pulse">Loading Setlist...</p>
    </div>

    <template v-else>
      <!-- Header -->
      <div class="flex items-center gap-4">
        <Button icon="pi pi-arrow-left" text rounded severity="secondary" @click="handleBack" />
        <div>
          <p class="rd-label leading-none mb-1">Setlist</p>
          <div class="flex items-center gap-2">
            <h1 class="text-2xl font-black text-slate-900 leading-tight">{{ setlist.name }}</h1>
            <Button v-if="bandStore.canEditSetlists" icon="pi pi-pencil" text rounded severity="secondary" size="small" class="opacity-40 hover:opacity-100" v-tooltip.top="'Edit Setlist Name'" @click="openEditDialog" />
          </div>
        </div>
      </div>

      <!-- Songs Section -->
      <div class="mt-8 rd-card-inner border-slate-300 overflow-hidden">
        <div class="p-6">
          <div class="flex items-center justify-between mb-4">
            <span class="text-sm font-bold text-slate-600">Songs</span>
            <span class="text-[10px] font-bold text-slate-300">{{ songs.length }} Total</span>
          </div>

          <!-- Add Song -->
          <div v-if="bandStore.canEditSetlists" class="flex gap-2 mb-4">
            <Select v-model="selectedSongId" :options="availableSongs" optionLabel="title" optionValue="id" placeholder="Select a song to add..." class="flex-1 text-sm" size="small" :disabled="availableSongs.length === 0" />
            <Button icon="pi pi-plus" size="small" severity="primary" :loading="addingSong" :disabled="!selectedSongId" @click="handleAddSong" />
          </div>

          <!-- Song List with Draggable -->
          <draggable
            v-model="songs"
            item-key="id"
            handle=".drag-handle"
            @end="handleReorder"
            class="flex flex-col gap-2"
          >
            <template #item="{element: song, index}">
              <div class="group p-4 rounded-2xl border transition-all bg-slate-50/50 border-slate-400 hover:border-teal-600 hover:shadow-md hover:shadow-teal-500/5 cursor-pointer" @click="emit('open-teleprompter', song.songId)">
                <div class="flex items-center justify-between">
                  <div class="flex items-center gap-4">
                    <div v-if="bandStore.canEditSetlists" class="drag-handle cursor-move p-1 text-slate-400 hover:text-slate-600 transition-colors" v-tooltip.top="'Drag to reorder'">
                      <i class="pi pi-bars"></i>
                    </div>
                    <div class="w-8 h-8 rounded-full bg-white flex items-center justify-center text-[10px] font-bold text-slate-400 group-hover:bg-teal-50 group-hover:text-teal-600 transition-colors border border-slate-300 shadow-sm">
                      {{ index + 1 }}
                    </div>
                    <div class="flex flex-col">
                      <span class="text-sm font-bold text-slate-700 group-hover:text-slate-900 transition-colors">{{ song.songTitle }}</span>
                      <span v-if="song.artist" class="text-[10px] text-slate-400">{{ song.artist }}</span>
                    </div>
                  </div>
                  <div class="flex items-center gap-1">
                    <Button v-if="bandStore.canEditSetlists" icon="pi pi-times" severity="danger" text rounded size="small"
                            class="opacity-60 hover:opacity-100 transition-opacity"
                            v-tooltip.top="'Remove from setlist'"
                            @click.stop="confirmRemoveSong(song.id, song.songTitle)" />
                  </div>
                </div>
                <div class="flex justify-center gap-0.5 mt-1">
                  <button
                    class="flex items-center gap-0.5 px-1.5 py-0.5 rounded-lg text-xs transition-all"
                    :class="song.currentUserVote === 'like' ? 'bg-teal-100 text-teal-700 font-bold' : 'text-slate-400 hover:text-teal-600 hover:bg-teal-50'"
                    :disabled="votingId === song.id"
                    @click.stop="handleVote(song.id, true)"
                  >
                    <i class="pi pi-thumbs-up text-[11px]"></i>
                    <span v-if="song.likes > 0">{{ song.likes }}</span>
                  </button>
                  <button
                    class="flex items-center gap-0.5 px-1.5 py-0.5 rounded-lg text-xs transition-all"
                    :class="song.currentUserVote === 'dislike' ? 'bg-red-100 text-red-700 font-bold' : 'text-slate-400 hover:text-red-500 hover:bg-red-50'"
                    :disabled="votingId === song.id"
                    @click.stop="handleVote(song.id, false)"
                  >
                    <i class="pi pi-thumbs-down text-[11px]"></i>
                    <span v-if="song.dislikes > 0">{{ song.dislikes }}</span>
                  </button>
                </div>
              </div>
            </template>
          </draggable>

          <div v-if="songs.length === 0" class="py-12 text-center border-2 border-dashed border-slate-300 rounded-3xl bg-slate-50/30">
            <div class="w-16 h-16 bg-white rounded-2xl shadow-sm flex items-center justify-center mx-auto mb-4 text-slate-200">
              <i class="pi pi-list text-2xl"></i>
            </div>
            <p class="text-xs text-slate-400 italic">No songs in this setlist</p>
          </div>
        </div>
      </div>
    </template>

    <!-- Remove Song Confirm Dialog -->
    <Dialog v-model:visible="showRemoveConfirm" modal :closable="false" :style="{ width: '22rem' }">
      <div class="flex flex-col items-center text-center py-6">
        <div class="w-32 h-32 bg-red-50 rounded-full flex items-center justify-center mb-6">
          <i class="pi pi-exclamation-triangle text-red-500" style="font-size: 5rem"></i>
        </div>
        <h2 class="text-xl font-bold text-slate-900 mb-8">Are you sure?</h2>
        <p class="text-sm text-slate-500 mb-8 px-4">This will remove <strong>"{{ songToRemove?.title }}"</strong> from the setlist.</p>

        <div class="flex flex-col gap-3 w-full">
          <Button label="Yes, Remove Song" severity="danger" class="w-full py-3 font-bold" @click="handleRemoveSong" />
          <Button label="Cancel" severity="secondary" text class="w-full font-medium" @click="showRemoveConfirm = false" />
        </div>
      </div>
    </Dialog>

    <!-- Edit Setlist Name Dialog -->
    <Dialog v-model:visible="showEditDialog" modal header="Edit Setlist Name" :style="{ width: '25rem' }">
      <div class="flex flex-col gap-4 py-2">
        <div class="flex flex-col gap-2">
          <label for="editSetlistName" class="font-semibold text-sm">Setlist Name</label>
          <InputText id="editSetlistName" v-model="editName" class="w-full" @keyup.enter="handleUpdateSetlist" />
        </div>
        <div class="flex justify-end gap-2 mt-4">
          <Button type="button" label="Cancel" severity="secondary" text @click="showEditDialog = false" />
          <Button type="button" label="Save" :loading="isUpdating" @click="handleUpdateSetlist" />
        </div>
      </div>
    </Dialog>
  </div>
</template>
